import React, { useMemo, useState } from 'react';
import {
  flexRender,
  useTable,
  type ColumnDef,
  type SortingState,
  type ExpandedState,
} from '@tanstack/react-table';

import { DebouncedInput } from './TableFilter';
import { appTableFeatures, type AppTableFeatures } from './tableFeatures';

export default function NestedMobileList<T extends { [key: string]: any }>({
  showPagination,
  // noTablePadding,
  // fontSize,
  data,
  columns1,
  // columns2,
  // nestedClass,
  // columnResizeMode = "onChange",
  // showFilters = false,
  // alignCells = "left",
  // noEmptyRows,
  // allowWordWrap = false,
  // subRowProp,
}: {
  showPagination?: boolean;
  noTablePadding?: boolean;
  fontSize?: string;
  data: T[];
  columns1: ColumnDef<AppTableFeatures, T>[];
  columns2?: ColumnDef<AppTableFeatures, T>[];
  columnResizeMode?: string;
  showFilters?: boolean;
  alignCells?: string;
  noEmptyRows?: boolean;
  allowWordWrap?: boolean;
  subRowProp?: keyof T;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const dataForTable = useMemo(() => data, [data]);
  const columns: ColumnDef<AppTableFeatures, T>[] = useMemo(
    () => columns1,
    [columns1],
  );

  const table = useTable({
    features: appTableFeatures,
    columns,
    data: dataForTable,
    state: {
      sorting,
      expanded,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: dataForTable.length > 9 ? 10 : dataForTable.length,
      },
    },
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    paginateExpandedRows: false,
    getRowCanExpand: (row) =>
      row.original.subRowProp !== null && row.original.subRowProp?.length > 0,
  });

  return (
    <div className='mb-4  w-full overflow-x-hidden border-l-[1px] border-l-gray-200 ps-2 dark:border-l-gray-700'>
      <div className='relative w-full'>
        <div className='mb-4 p-1'>
          <DebouncedInput
            type='search'
            aria-label='Search all columns'
            value={table.state.globalFilter ?? ''}
            onChange={(value) => table.setGlobalFilter(String(value))}
            placeholder='Search all columns...'
          />
        </div>
        {/* <div className="p-1">
          <div id="accordion-open" data-accordion="open" className="my-1 mb-4">
            <h2 id="accordion-open-heading-1" className="m-0">
              <button
                type="button"
                className={`flex w-full items-center justify-between gap-3  border ${
                  isOpen
                    ? "rounded-b-none rounded-t-lg border-b-0"
                    : "rounded-lg"
                } g-gray-50  block w-full  rounded-lg border border-gray-300 p-2.5
              text-sm text-gray-900 focus:border-gray-400 focus:ring-gray-400
               dark:border-0 dark:bg-gray-700 dark:text-gray-400 dark:placeholder-gray-400 dark:focus:ring-gray-400`}
                data-accordion-target="#accordion-open-body-1"
                aria-expanded={isOpen}
                aria-controls="accordion-open-body-1"
                onClick={() => setIsOpen(!isOpen)}
              >
                <span className="flex items-center">Sorting</span>
                <svg
                  data-accordion-icon
                  className="h-3 w-3 shrink-0 rotate-180"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 10 6"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5 5 1 1 5"
                  />
                </svg>
              </button>
            </h2>
            <div
              id="accordion-open-body-1"
              className={`${isOpen ? "block" : "hidden"}`}
              aria-labelledby="accordion-open-heading-1"
            >
              <div className="rounded-b-lg border border-gray-200 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex flex-wrap justify-between gap-2 px-1">
                  {table.getHeaderGroups().map((headerGroup) =>
                    headerGroup.headers.map((header) =>
                      header.isPlaceholder ? null : (
                        <button
                          key={header.id}
                          {...{
                            className: header.column.getCanSort()
                              ? "cursor-pointer select-none flex items-center justify-center border dark:border-0 rounded-md p-2 text-sm w-28 dark:bg-gray-800"
                              : "flex items-center justify-center",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {{
                            asc: (
                              <FaSortUp
                                style={{ marginLeft: ".5rem" }}
                              ></FaSortUp>
                            ),
                            desc: (
                              <FaSortDown
                                style={{ marginLeft: ".5rem" }}
                              ></FaSortDown>
                            ),
                            false: (
                              <FaSort style={{ marginLeft: ".5rem" }}></FaSort>
                            ),
                          }[header.column.getIsSorted() as string] ?? null}
                        </button>
                      ),
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </div> */}

        <ul className='p-1'>
          {table.getRowModel().rows.map((row, i) => {
            return (
              <React.Fragment key={i}>
                <li className='mb-4'>
                  <div
                    key={row.id}
                    className='block w-full rounded-lg border border-gray-200
                     bg-white p-6 shadow hover:bg-gray-100
                       dark:border-0 dark:bg-gray-800
                       dark:hover:bg-gray-700'
                  >
                    <div className='flex flex-row flex-wrap items-center'>
                      {row.getVisibleCells().map((cell) => {
                        if (!cell.getValue()) {
                          return '';
                        }
                        return (
                          <span
                            key={cell.id}
                            {...{
                              style: {
                                overflow: 'hidden',
                                padding: '5px 7px',
                              },
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </div>

      {showPagination && table.getCoreRowModel().rows.length > 0 && (
        <nav
          className='flex-column flex flex-wrap items-center justify-center gap-4 pt-4 sm:flex-row'
          aria-label='Table navigation'
        >
          <ul className=' flex h-8 text-sm '>
            <li>
              <button
                className='ms-0 flex h-8 
                items-center justify-center rounded-s-lg border border-gray-300 bg-white
                px-3 leading-tight  hover:bg-gray-100
                 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800 
                  dark:hover:bg-gray-700 dark:hover:text-white'
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </button>
            </li>

            <li>
              <div
                aria-current='page'
                className='ark:border-gray-700 flex h-8 
                w-[95px] items-center justify-center
                border border-gray-300 px-3 dark:border-gray-700 dark:bg-gray-800 '
              >
                <span className='mr-2 font-bold'>
                  {table.state.pagination.pageIndex + 1}
                </span>
                of <span className='ml-2'>{table.getPageCount()}</span>
              </div>
            </li>
            <li>
              <div>
                <select
                  className='m-0 block w-full border-gray-300 
              py-[5px] pr-6 text-sm shadow-sm focus:border-indigo-300 focus:ring
              focus:ring-indigo-200 focus:ring-opacity-0 dark:border-gray-700 dark:bg-gray-800 '
                  aria-label='rows per page'
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                  value={table.state.pagination.pageSize}
                >
                  <option value='5'>5 rows</option>
                  <option value='10'>10 rows</option>
                  <option value='20'>20 rows</option>
                  <option value='25'>25 rows</option>
                  <option value='50'>50 rows</option>
                  <option value='100'>100 rows</option>
                </select>
              </div>
            </li>

            <li>
              <button
                className='flex h-8 w-[79px] items-center justify-center 
                rounded-e-lg border border-gray-300 bg-white px-3 leading-tight
                  hover:bg-gray-100 hover:text-gray-700 
                 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800
                   dark:hover:bg-gray-700 dark:hover:text-white'
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </button>
            </li>
          </ul>

          <div className='text-sm font-semibold'>
            {table.getFilteredRowModel().flatRows.length ?? 'N/A'} results
          </div>
        </nav>
      )}
    </div>
  );
}
